import { styled } from "@mui/material/styles";
import { Box, Typography, TextField, Button } from "@mui/material";

export const CapsuleContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "200px",
  borderRadius: "15px",
  border: `1px solid ${theme.palette.custom.primaryPurple}`,
  alignItems: "center",
  backgroundColor: theme.palette.custom.lightPink,
  color: theme.palette.custom.primaryPurple,
  height: "40px",
  marginBottom: theme.spacing(2),
  marginLeft: "24px",
}));

export const AvailableCreditsText = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  width: "70%",
  height: "40px",
  margin: 0,
  fontSize: "12px",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRight: `1px solid ${theme.palette.custom.primaryPurple}`,
}));

export const UsedCreditsText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRed",
})(({ theme, isRed }) => ({
  textAlign: "center",
  width: "28%",
  fontWeight: 500,
  fontSize: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: isRed ? theme.palette.error.main : theme.palette.custom.primaryPurple,
}));

export const ContentContainer = styled(Box)(({ theme }) => ({
  width: 740,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
}));

export const HomeContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  minHeight: "calc(100vh - 600px)",
  width: "100%",
  gap: theme.spacing(3),
}));

export const TextAreaContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    width: "90%",
    maxWidth: 740,
  },

  "& .MuiInputBase-root": {
    backgroundColor: "#FFFFFF",
  },
}));

export const StyledTextField = styled(TextField)(({ theme, maxWidth }) => ({
  width: "100%",
  "& .MuiInputBase-root": {
    height: 213,
    borderRadius: "25px",
    backgroundColor: "#ffffff",
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  "& .MuiInputBase-input": {
    height: "calc(100% - 40px) !important",
    overflow: "auto !important",
    "&::-webkit-scrollbar": {
      width: "7px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.custom.primaryPurple,
      borderRadius: "3px",
    },
    // Firefox scrollbar
    scrollbarWidth: "thin",
    scrollbarColor: `${theme.palette.custom.primaryPurple} transparent`,
  },
  "& textarea": {
    maxWidth: maxWidth || undefined,
  },
  "& textarea::placeholder": {
    fontWeight: 500,
    fontSize: "15px",
    lineHeight: "18px",
    letterSpacing: "2px",
    color: theme.palette.custom.softPink,
    opacity: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderWidth: "0",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "0",
  },
}));

export const TextAreaFooter = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  position: "absolute",
  bottom: theme.spacing(1.5),
  right: theme.spacing(3),
  width: "auto",
}));

export const CharacterCounter = styled(Typography)({
  fontSize: "12px",
  color: "#b3b3b3",
});

export const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  gap: theme.spacing(2),
}));

export const ActionButton = styled(Button)(({ theme, halfWidth }) => ({
  flex: 1,
  width: "100%",
  backgroundColor: theme.palette.custom.primaryPurple,
  color: theme.palette.common.white,
  borderRadius: "15px",
  padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
  fontWeight: 400,
  fontSize: "18px",
  lineHeight: "21.6px",
  letterSpacing: "0.5px",
  textTransform: "none",
  maxWidth: halfWidth ? "50%" : "100%",

  "&:hover": {
    backgroundColor: theme.palette.custom.primaryBlue,
  },
  "&.Mui-disabled": {
    backgroundColor: "#DEDEDE",
    color: "#1D1B201A",
  },
  "& img": {
    marginRight: theme.spacing(1),
    width: "24px",
    height: "24px",
  },
}));

export const DisabledButtonWrapper = styled(Box)({
  display: "inline-block",
});

export const CopyIconContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: theme.spacing(1.5),
  left: theme.spacing(3),
  zIndex: 2,
  "& .copy-container": {
    display: "inline-block",
    position: "relative",
    "& .tooltip-container": {
      position: "absolute",
      display: "inline-block",
      "& .tooltip-box": {
        position: "absolute",
        backgroundColor: theme.palette.custom.lightPink,
        color: theme.palette.custom.primaryPurple,
        padding: "8px",
        borderRadius: "4px",
        whiteSpace: "nowrap",
        fontSize: "14px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
        pointerEvents: "none",
        bottom: "30px",
        left: "-10px",
        zIndex: 1000,
        opacity: 0,
        transition: "opacity 0.2s ease-in-out",
      },
    },
    "&:hover .tooltip-box": {
      opacity: 1,
      visibility: "visible",
    },
    "& .copy-icon": {
      width: "22px",
      cursor: "pointer",
      padding: "6px",
      borderRadius: "6px",
      "&:hover": {
        backgroundColor: "#fbceb159",
      },
    },
    "& .copy-icon-disabled": {
      opacity: 0.5,
      pointerEvents: "none",
    },
  },
}));

export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100dvw",
  height: "100dvh",
  marginBottom: theme.spacing(2),
}));

export const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "12px",
  marginBottom: theme.spacing(2),
}));

export const HeaderTextArea = styled(Box)({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexDirection: "row",
  marginBottom: "12px",
});

export const CreditsCapsule = ({ available = 0, used = 0 }) => {
  const isNoCredits = available === 0;

  return (
    <CapsuleContainer>
      <AvailableCreditsText>{available} Credits available</AvailableCreditsText>
      <UsedCreditsText isRed={isNoCredits}>{used} used</UsedCreditsText>
    </CapsuleContainer>
  );
};

export const LanguageCapsuleContainer = styled(Box)(({ opacity }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "75px",
  height: "25px",
  backgroundColor: "#ffffff",
  whiteSpace: "nowrap",
  opacity, // Dynamically control opacity
  transition: "opacity 0.3s ease-in-out",
  borderRadius: "5px",
}));
export const BetaCapsule = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "48px",
  height: "22px",
  backgroundColor: "#FB4764",
  marginLeft: "19px",
  borderRadius: "20px",
}));

export const BetaText = styled(Typography)(({ theme }) => ({
  color: "#FFFFFF",
  fontSize: "10px",
  fontFamily: "Inter",
  fontWeight: 400,
}));

export const LanguageText = styled(Typography)(({ theme }) => ({
  color: "#b3b3b3",
  fontSize: "14px",
  fontFamily: "Roboto",
  fontWeight: 400,
}));

export const Chips = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  position: "absolute",
  bottom: theme.spacing(1.5),
  left: theme.spacing(3),
  zIndex: 2,
}));

export const Chip = styled(Box)(({ theme }) => ({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "nowrap",
  backgroundColor: theme.palette.custom.white,
  color: theme.palette.custom.primaryPurple,
  border: `1px solid ${theme.palette.custom.primaryPurple}`,
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  fontSize: "12px",
  fontWeight: 500,
  borderRadius: "20px",

  "&:hover": {
    backgroundColor: "#EFE1E2",
    color: theme.palette.custom.primaryPurple,
  },
}));
